/**
 * Data Sanitization for Error Logging
 *
 * Removes PII and sensitive data before logging errors
 *
 * @see /docs/ERROR_BOUNDARY_IMPROVEMENTS.md for security guidelines
 */
import type { ErrorContext } from '@/types/errors';

import {
  CREDIT_CARD_REGEX,
  EMAIL_REGEX,
  IP_REGEX,
  NEVER_LOG,
  TOKEN_REGEX,
} from './constants';

/**
 * Sanitize error context before logging
 */
export function sanitizeContext(
  context: Partial<ErrorContext>
): Partial<ErrorContext> {
  return {
    ...context,
    user: context.user
      ? {
          id: hashValue(context.user.id),
          email: maskEmail(context.user.email),
        }
      : undefined,
    storeSnapshot: context.storeSnapshot
      ? sanitizeObject(context.storeSnapshot)
      : undefined,
    props: context.props ? sanitizeObject(context.props) : undefined,
  };
}

/**
 * Sanitize any object by recursively removing sensitive fields
 */
export function sanitizeObject(
  obj: Record<string, unknown>,
  seen: WeakSet<object> = new WeakSet()
): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    // Check if key should never be logged (case-insensitive)
    const lowerKey = key.toLowerCase();
    const shouldRedact = Object.keys(NEVER_LOG).some(
      neverLogKey => neverLogKey.toLowerCase() === lowerKey
    );

    if (shouldRedact) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Recursively sanitize nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      // Check for circular reference
      if (seen.has(value as object)) {
        sanitized[key] = '[Circular]';
      } else {
        seen.add(value as object);
        sanitized[key] = sanitizeObject(value as Record<string, unknown>, seen);
      }
    }
    // Sanitize arrays
    else if (Array.isArray(value)) {
      sanitized[key] = value.map(item =>
        typeof item === 'object' && item !== null
          ? seen.has(item)
            ? '[Circular]'
            : (() => {
                seen.add(item);
                return sanitizeObject(item as Record<string, unknown>, seen);
              })()
          : sanitizeValue(key, item)
      );
    }
    // Sanitize primitive values
    else {
      sanitized[key] = sanitizeValue(key, value);
    }
  }

  return sanitized;
}

/**
 * Sanitize individual values
 * @internal
 */
export function sanitizeValue(key: string, value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }

  const lowerKey = key.toLowerCase();

  // Check if key suggests sensitive data
  if (lowerKey.includes('password') || lowerKey.includes('secret')) {
    return '[REDACTED]';
  }

  if (lowerKey.includes('token') || lowerKey.includes('auth')) {
    return maskToken(value);
  }

  if (lowerKey.includes('email')) {
    return maskEmail(value);
  }

  if (lowerKey.includes('ip') || lowerKey.includes('address')) {
    return maskIP(value);
  }

  // Pattern-based sanitization using sanitizeString
  return sanitizeString(value);
}

/**
 * Mask email addresses
 * Example: user@example.com → us**@example.com
 */
export function maskEmail(email: string): string {
  const parts = email.split('@');
  if (parts.length !== 2) {
    return '[EMAIL]';
  }

  const [username, domain] = parts;
  if (username.length === 1) {
    return `${username}***@${domain}`;
  }

  return `${username.substring(0, 2)}***@${domain}`;
}

/**
 * Mask tokens (show first and last 4 chars)
 * Example: eyJhbGciOiJIUz... → eyJh...UzI1
 */
export function maskToken(token: string): string {
  if (token.length <= 8) {
    return '[TOKEN]';
  }

  return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
}

/**
 * Mask IP addresses (show first two octets)
 * Example: 192.168.1.100 → 192.168.*.*
 */
export function maskIP(ip: string): string {
  const parts = ip.split('.');
  if (parts.length !== 4) {
    return '[IP]';
  }

  return `${parts[0]}.${parts[1]}.*.*`;
}

/**
 * Mask credit card numbers
 * Example: 4532015112830366 → ****-****-****-0366
 */
export function maskCreditCard(card: string): string {
  // Remove spaces and dashes
  const cleaned = card.replace(/[\s-]/g, '');

  // Validate it looks like a card number (13-19 digits)
  if (!/^\d{13,19}$/.test(cleaned)) {
    return '[CARD]';
  }

  // Show last 4 digits
  const last4 = cleaned.slice(-4);
  return `****-****-****-${last4}`;
}

/**
 * Sanitize a string by masking all PII patterns
 * Processes patterns in order of specificity to avoid false matches
 * Example: "User alice@test.com from 192.168.1.1" → "User al***@test.com from 192.168.*.*"
 */
export function sanitizeString(value: string): string {
  let sanitized = value;

  // Process in order of specificity: tokens first (to avoid IP regex matching JWT dots)
  sanitized = sanitized.replace(TOKEN_REGEX, match => maskToken(match));
  sanitized = sanitized.replace(EMAIL_REGEX, match => maskEmail(match));
  sanitized = sanitized.replace(CREDIT_CARD_REGEX, match =>
    maskCreditCard(match)
  );
  sanitized = sanitized.replace(IP_REGEX, match => maskIP(match));

  return sanitized;
}

/**
 * Hash sensitive values (for tracking without exposing data)
 * Uses simple deterministic hash for consistent tracking IDs
 *
 * Note: This is NOT cryptographically secure. For security-sensitive hashing
 * of passwords or tokens, use hashValueSecure() instead.
 *
 * @example
 * ```typescript
 * const userId = 'user-12345';
 * const hash = hashValue(userId); // → 'a3f2e1b9c4d5e6f7'
 * // Same input always produces same hash
 * ```
 */
export function hashValue(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16);
}

/**
 * Cryptographically secure hash using SHA-256
 * Use this for security-sensitive scenarios
 *
 * @param value - String to hash
 * @returns Promise resolving to first 16 hex characters of SHA-256 hash
 *
 * @example
 * ```typescript
 * const userId = 'user-12345';
 * const hash = await hashValueSecure(userId); // → 'a3f2e1b9c4d5e6f7'
 * ```
 */
export async function hashValueSecure(value: string): Promise<string> {
  // Use Web Crypto API (available in modern browsers and Node 15+)
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    return hashHex.substring(0, 16); // Return first 16 characters
  }

  // Fallback for environments without Web Crypto API
  return hashValue(value);
}

/**
 * Remove circular references from objects
 */
export function removeCircularReferences(
  obj: unknown,
  seen = new WeakSet()
): unknown {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (seen.has(obj as object)) {
    return '[Circular]';
  }

  seen.add(obj as object);

  if (Array.isArray(obj)) {
    return obj.map(item => removeCircularReferences(item, seen));
  }

  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    cleaned[key] = removeCircularReferences(value, seen);
  }

  return cleaned;
}
